import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReservationByIdAndEmail, cancelReservationById, updateReservationByCustomer } from '../utils/api';
import { formatDateTime, toApiDate } from '../utils/date';
import Calendar from '../components/Calendar';
import ReservationForm from '../components/ReservationForm';
import './InfoPages.css';
import './ManageReservationPage.css';

const STEPS = {
  SEARCH: 'search',
  VIEW: 'view',
  EDIT_DATETIME: 'edit_datetime',
  EDIT_CONTACT: 'edit_contact',
  CANCEL_CONFIRM: 'cancel_confirm',
  UPDATE_SUCCESS: 'update_success'
};

// Example placeholders
const RESERVATION_ID_EXAMPLES = [
  'Rezervačné číslo',
  'R-260202-ABC123',
];

const EMAIL_EXAMPLES = [
  'Rezervačný email',
  'johndoe@example.com',
];

const ManageReservationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(STEPS.SEARCH);
  const [previousStep, setPreviousStep] = useState(null);
  const [formData, setFormData] = useState({
    rezervacneId: '',
    email: ''
  });
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [editDisabledReason, setEditDisabledReason] = useState(null);
  
  const [editedDate, setEditedDate] = useState(null);
  const [editedTime, setEditedTime] = useState(null);
  const [editedContactInfo, setEditedContactInfo] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [reservationIdIndex, setReservationIdIndex] = useState(0);
  const [reservationIdFade, setReservationIdFade] = useState(false);
  const [emailIndex, setEmailIndex] = useState(0);
  const [emailFade, setEmailFade] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('id');
    const emailParam = urlParams.get('email');
    
    if (idParam && emailParam) {
      setFormData({
        rezervacneId: idParam,
        email: emailParam
      });
      setLoading(true);
      setTimeout(async () => {
        try {
          const response = await getReservationByIdAndEmail(idParam, emailParam);
          if (response.data.success) {
            setReservation(response.data.data.reservation);
            setCanEdit(response.data.data.can_edit);
            setEditDisabledReason(response.data.data.edit_disabled_reason);
            setCurrentStep(STEPS.VIEW);
          } else {
            setError(response.data.message || 'Rezervácia nebola nájdená');
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Nepodarilo sa načítať rezerváciu');
        } finally {
          setLoading(false);
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    const idInterval = setInterval(() => {
      if (!formData.rezervacneId) {
        setReservationIdFade(true);
        
        setTimeout(() => {
          setReservationIdIndex((prev) => (prev + 1) % RESERVATION_ID_EXAMPLES.length);
          setReservationIdFade(false);
        }, 500);
      }
    }, 10000);

    const emailInterval = setInterval(() => {
      if (!formData.email) {
        setEmailFade(true);
        
        setTimeout(() => {
          setEmailIndex((prev) => (prev + 1) % EMAIL_EXAMPLES.length);
          setEmailFade(false);
        }, 500);
      }
    }, 10000);

    return () => {
      clearInterval(idInterval);
      clearInterval(emailInterval);
    };
  }, [formData.rezervacneId, formData.email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await getReservationByIdAndEmail(
        formData.rezervacneId.trim().toUpperCase(),
        formData.email.trim()
      );
      
      if (response.data.success) {
        const res = response.data.data.reservation;
        setReservation(res);
        setCanEdit(response.data.data.can_edit);
        setEditDisabledReason(response.data.data.edit_disabled_reason);
        setEditedDate(null);
        setEditedTime(null);
        setEditedContactInfo({
          cele_meno: res.cele_meno,
          email: res.email,
          telefon: res.telefon,
          adresa: res.adresa || '',
          poznamka: res.poznamka || ''
        });
        setCurrentStep(STEPS.VIEW);
      } else {
        setError(response.data.message || 'Rezervácia nebola nájdená');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Rezervácia s týmto číslom a emailom nebola nájdená'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateTimeSelect = (date, time) => {
    setEditedDate(date);
    setEditedTime(time);
    setHasChanges(true);
  };

  const handleContactSubmit = (data) => {
    setEditedContactInfo(data);
    handleSaveChanges(data);
  };

  const handleSaveChanges = async (contactData = editedContactInfo) => {
    setLoading(true);
    setError('');

    try {
      const updateData = {
        datum_rezervacie: toApiDate(editedDate),
        cas_rezervacie: editedTime,
        cele_meno: contactData.cele_meno,
        telefon: contactData.telefon,
        adresa: contactData.adresa,
        poznamka: contactData.poznamka
      };

      const response = await updateReservationByCustomer(reservation.id, updateData);
      
      if (response.data.success) {
        const refreshResponse = await getReservationByIdAndEmail(
          formData.rezervacneId.trim().toUpperCase(),
          formData.email.trim()
        );
        if (refreshResponse.data.success) {
          setReservation(refreshResponse.data.data.reservation);
        }
        setHasChanges(false);
        setCurrentStep(STEPS.UPDATE_SUCCESS);
      } else {
        setError(response.data.message || 'Nepodarilo sa upraviť rezerváciu');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Nepodarilo sa upraviť rezerváciu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await cancelReservationById(reservation.id);
      
      if (response.data.success) {
        navigate('/');
      } else {
        setError(response.data.message || 'Nepodarilo sa zrušiť rezerváciu');
        setCurrentStep(STEPS.VIEW);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Nepodarilo sa zrušiť rezerváciu');
      setCurrentStep(STEPS.VIEW);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSearch = () => {
    navigate('/moja-rezervacia', { replace: true });
    setCurrentStep(STEPS.SEARCH);
    setReservation(null);
    setFormData({ rezervacneId: '', email: '' });
    setError('');
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'NEPOTVRDENÁ',
      confirmed: 'POTVRDENÁ',
      completed: 'DOKONČENÁ',
      cancelled: 'ZRUŠENÁ'
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'badge-warning',
      confirmed: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return `badge ${classes[status] || 'badge-secondary'}`;
  };

  const handleEdit = (field) => {
    setPreviousStep(STEPS.VIEW);
    setCurrentStep(STEPS.EDIT_CONTACT);
    setTimeout(() => {
      const input = document.getElementById(field);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SEARCH:
        return (
          <div className="info-content">
            <div className="step-card">
              <p className="step-subtitle">
                Zadajte svoje rezervačné číslo a emailovú adresu pre zobrazenie detailov rezervácie. Po autentifikácií budete môcť svoju rezerváciu upraviť alebo v prípade potreby zrušiť.
              </p>

              <form onSubmit={handleSearch} className="search-form">
                <div className={`form-group-floating ${formData.rezervacneId ? 'has-value' : ''}`}>
                  <input
                    type="text"
                    id="rezervacneId"
                    name="rezervacneId"
                    className={`form-input-floating ${reservationIdFade ? 'label-fade' : ''}`}
                    value={formData.rezervacneId}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="rezervacneId" className="form-label-floating dynamic-label">
                    {RESERVATION_ID_EXAMPLES[reservationIdIndex]}
                  </label>
                </div>

                <div className={`form-group-floating ${formData.email ? 'has-value' : ''}`}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input-floating ${emailFade ? 'label-fade' : ''}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="email" className="form-label-floating dynamic-label">
                    {EMAIL_EXAMPLES[emailIndex]}
                  </label>
                </div>

                {error && (
                  <div className="alert alert-error">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg btn-block"
                  disabled={loading}
                >
                  {loading ? 'Vyhľadávám...' : 'Vyhľadať rezerváciu'}
                </button>
              </form>
            </div>
          </div>
        );

      case STEPS.VIEW:
        if (!reservation) return null;
        
        const isCancelled = reservation.stav === 'cancelled';

        return (
          <div className="info-content">
            <div className="summary-card">
              {/* Reservation ID Header */}
              <div className="reservation-id-header">
                <div className="reservation-id-label">Rezervačné číslo</div>
                <div className="reservation-id-value">{reservation.rezervacne_id}</div>
                <div className={getStatusBadgeClass(reservation.stav)}>
                  {getStatusLabel(reservation.stav)}
                </div>
              </div>

              {/* Service Info */}
              <div className="summary-section">
                <div className="section-title">Zhrnutie rezervácie</div>
                <div className="summary-row">
                  <div className="row-label">Služba</div>
                  <div className="row-value">
                    <span>{reservation.nazov_sluzby}</span>
                  </div>
                </div>
                <div className="summary-row">
                  <div className="row-label">Dátum a čas</div>
                  <div className="row-value">
                    <span className={editedDate || editedTime ? 'value-changed' : ''}>
                      {formatDateTime(
                        editedDate ? new Date(editedDate) : new Date(reservation.datum_rezervacie), 
                        editedTime || reservation.cas_rezervacie
                      )}
                    </span>
                    {!isCancelled && (
                      <button 
                        className={`edit-btn ${!canEdit ? 'disabled' : ''}`}
                        onClick={() => canEdit && setCurrentStep(STEPS.EDIT_DATETIME)}
                        disabled={!canEdit}
                        title={!canEdit ? 'Momentálne nedostupné' : ''}
                      >
                        Zmeniť dátum/čas
                      </button>
                    )}
                  </div>
                </div>
                <div className="summary-row">
                  <div className="row-label">Trvanie</div>
                  <div className="row-value">
                    <span>{reservation.trvanie_minuty} minút</span>
                  </div>
                </div>
                {reservation.sluzba_cena && (
                  <div className="summary-row">
                    <div className="row-label">Cena</div>
                    <div className="row-value">
                      <span>{reservation.sluzba_cena} €</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="summary-section">
                <div className="section-title">Kontaktné údaje</div>
                <div className="summary-row">
                  <div className="row-label">Meno</div>
                  <div className="row-value">
                    <span>{reservation.cele_meno}</span>
                    {!isCancelled && (
                      <button 
                        className={`edit-btn ${!canEdit ? 'disabled' : ''}`}
                        onClick={() => canEdit && handleEdit('cele_meno')}
                        disabled={!canEdit}
                        title={!canEdit ? 'Momentálne nedostupné' : ''}
                      >
                        Zmeniť meno
                      </button>
                    )}
                  </div>
                </div>
                <div className="summary-row">
                  <div className="row-label">Email</div>
                  <div className="row-value">
                    <span>{reservation.email}</span>
                  </div>
                </div>
                <div className="summary-row">
                  <div className="row-label">Telefón</div>
                  <div className="row-value">
                    <span>{reservation.telefon}</span>
                    {!isCancelled && (
                      <button 
                        className={`edit-btn ${!canEdit ? 'disabled' : ''}`}
                        onClick={() => canEdit && handleEdit('telefon')}
                        disabled={!canEdit}
                        title={!canEdit ? 'Momentálne nedostupné' : ''}
                      >
                        Zmeniť telefón
                      </button>
                    )}
                  </div>
                </div>
                {reservation.adresa && (
                  <div className="summary-row">
                    <div className="row-label">Adresa</div>
                    <div className="row-value">
                      <span style={{whiteSpace: 'pre-line'}}>{reservation.adresa}</span>
                      {!isCancelled && (
                        <button 
                          className={`edit-btn ${!canEdit ? 'disabled' : ''}`}
                          onClick={() => canEdit && handleEdit('adresa')}
                          disabled={!canEdit}
                          title={!canEdit ? 'Momentálne nedostupné' : ''}
                        >
                          Zmeniť adresu
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {reservation.poznamka && (
                  <div className="summary-row">
                    <div className="row-label">Poznámka</div>
                    <div className="row-value">
                      <span>{reservation.poznamka}</span>
                      {!isCancelled && (
                        <button 
                          className={`edit-btn ${!canEdit ? 'disabled' : ''}`}
                          onClick={() => canEdit && handleEdit('poznamka')}
                          disabled={!canEdit}
                          title={!canEdit ? 'Momentálne nedostupné' : ''}
                        >
                          Zmeniť poznámku
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {!canEdit && !isCancelled && (
                <div className="alert alert-warning">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {editDisabledReason === 'disabled' 
                    ? 'Úprava ani zrušenie rezervácie momentálne nie sú možné.'
                    : 'Čas na úpravu alebo zrušenie tejto rezervácie už vypršal.'
                  }
                </div>
              )}

              {canEdit && !isCancelled && reservation.stav === 'confirmed' && (
                <div className="alert alert-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Každá zmena dátumu, času alebo adresy podlieha novému potvrdeniu rezervácie.
                </div>
              )}

              {isCancelled && (
                <div className="alert alert-warning">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Táto rezervácia bola zrušená
                </div>
              )}

              {error && (
                <div className="alert alert-error mb-3">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="summary-actions">
                <button 
                  onClick={handleBackToSearch}
                  className="btn btn-secondary"
                >
                  Späť na vyhľadávanie
                </button>
                
                {!isCancelled && hasChanges && (
                  <button 
                    onClick={() => canEdit && handleSaveChanges()}
                    className={`btn btn-primary ${!canEdit ? 'disabled' : ''}`}
                    disabled={!canEdit || loading}
                  >
                    {loading ? 'Ukladám...' : 'Uložiť zmeny'}
                  </button>
                )}
                
                {!isCancelled && !hasChanges && (
                  <button 
                    onClick={() => canEdit && setCurrentStep(STEPS.CANCEL_CONFIRM)}
                    className={`btn btn-danger ${!canEdit ? 'disabled' : ''}`}
                    disabled={!canEdit}
                    title={!canEdit ? 'Momentálne nedostupné' : ''}
                  >
                    Zrušiť rezerváciu
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case STEPS.EDIT_DATETIME:
        return (
          <div className="calendar-container">
            <Calendar
              selectedService={{
                id: reservation.sluzba_id,
                nazov: reservation.nazov_sluzby,
                trvanie_minuty: reservation.trvanie_minuty
              }}
              onDateTimeSelect={handleDateTimeSelect}
              onBack={() => setCurrentStep(STEPS.VIEW)}
              onNext={() => setCurrentStep(STEPS.VIEW)}
              selectedDate={editedDate}
              selectedTime={editedTime}
            />
          </div>
        );

      case STEPS.EDIT_CONTACT:
        return (
          <ReservationForm
            onSubmit={handleContactSubmit}
            onBack={() => setCurrentStep(previousStep || STEPS.EDIT_DATETIME)}
            initialData={editedContactInfo}
            loading={loading}
            emailReadOnly={true}
          />
        );

      case STEPS.CANCEL_CONFIRM:
        return (
          <div className="info-content">
            <div className="summary-card confirm-dialog">
              <div className="confirm-icon danger">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              
              <h2>Zrušiť rezerváciu?</h2>
              <p className="confirm-message">
                Naozaj chcete zrušiť rezerváciu <strong>{reservation.rezervacne_id}</strong> ?
              </p>
              {/* <div className="confirm-details">
                <p>{formatDateTime(new Date(reservation.datum_rezervacie), reservation.cas_rezervacie)}</p>
                <p>{reservation.nazov_sluzby}</p>
              </div> */}

              {error && (
                <div className="alert alert-error mb-3">
                  {error}
                </div>
              )}

              <div className="summary-actions">
                <button 
                  onClick={() => {
                    setCurrentStep(STEPS.VIEW);
                    setError('');
                  }}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Nie, zachovať
                </button>
                <button 
                  onClick={handleCancelReservation}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  {loading ? 'Ruším...' : 'Áno, zrušiť'}
                </button>
              </div>
            </div>
          </div>
        );

      case STEPS.UPDATE_SUCCESS:
        return (
          <div className="info-content">
            <div className="summary-card confirm-dialog">
              <div className="confirm-icon warning">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </div>
              
              <h2 style={{color: '#d97706'}}>Rezervácia upravená</h2>
              <p className="confirm-message">
                Vaša rezervácia bola úspešne upravená. Na email Vám bolo odoslané potvrdenie.
              </p>

              <div className="summary-actions">
                <button 
                  onClick={handleBackToSearch}
                  className="btn btn-secondary"
                >
                  Späť na vyhľadávanie
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="info-page manage-reservation-page">
      <div className="container-sm">
        <div className="info-hero">
          <h1>Moja rezervácia</h1>
          <p className="info-hero-subtitle">
            Skontrolujte, upravte alebo zrušte termín jednoducho na jednom mieste.
          </p>
        </div>
        {currentStep !== STEPS.EDIT_DATETIME && renderStep()}
      </div>
      
      {/* Calendar outside container-sm for full width like ReservationPage */}
      {currentStep === STEPS.EDIT_DATETIME && renderStep()}
    </div>
  );
};

export default ManageReservationPage;
